import { cx } from '@linaria/core';
import { useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { resets } from '@ui/css';
import { IsomorphicPortal, CSSRefTransition, useSelf } from '@utils/react';
import { SnackbarItem } from './Item.js';
import { snackbarCls, transitionCls, ITEM_TRANSITION_DURATION_MS, ITEM_TRANSITION_DELAY_MS } from './Snackbar.style.js';
import { jsx } from 'react/jsx-runtime';

const ITEM_TTL_MS = 8000;
/**
 * Renders urgent messages once for a limitied duration.
 *
 * Notifications are tracked on their title, ensuring that the same
 * notification is never rendered more than once.
 */
const Snackbar = ({
  notifications
}) => {
  const [activeNotifications, closeNotification] = useActiveNotifications(notifications);
  return /*#__PURE__*/jsx(IsomorphicPortal, {
    children: /*#__PURE__*/jsx("article", {
      className: cx(snackbarCls, resets.rootCls),
      role: activeNotifications.length ? undefined : "none",
      children: /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: activeNotifications.map((it, index) => /*#__PURE__*/jsx(CSSRefTransition, {
          classNames: transitionCls,
          timeout: ITEM_TRANSITION_DURATION_MS + index * ITEM_TRANSITION_DELAY_MS,
          children: ref => /*#__PURE__*/jsx(SnackbarItem, {
            ref: ref,
            notification: it,
            index: index,
            closeNotification: closeNotification
          })
        }, it.title))
      })
    })
  });
};
function useActiveNotifications(notifications) {
  const [activeNotifications, setActiveNotifications] = useState([]);
  const self = useSelf({
    seenNotifications: new Set()
  });
  useEffect(() => {
    const newNotifications = [...activeNotifications];
    for (const notification of notifications ?? []) {
      // We only add new notifications in case they haven't been seen before.
      if (!self.seenNotifications.has(notification.title)) {
        newNotifications.push(notification);
        self.seenNotifications.add(notification.title);

        // Once the notification has been displayed for its desired duration,
        // then remove it!
        setTimeout(() => {
          setActiveNotifications(notifications => removeNotification(notifications, notification));
        }, ITEM_TTL_MS);
      }
    }
    setActiveNotifications(newNotifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);
  const closeNotification = notification => {
    setActiveNotifications(notifications => removeNotification(notifications, notification));
  };
  return [activeNotifications, closeNotification];
}
function removeNotification(notifications, toRemove) {
  const newNotifications = [...notifications];
  const index = newNotifications.findIndex(it => it.title === toRemove.title);
  if (index !== -1) {
    newNotifications.splice(index, 1);
  }
  return newNotifications;
}

export { Snackbar };
//# sourceMappingURL=Snackbar.js.map
